/**
 * Journey Registry
 *
 * Maps (goal, gender, pathway, modality) combinations to journey definitions.
 *
 * For most goals, the journey is determined by goal + gender + pathway.
 * For pregnancy, an additional dimension — treatment modality — determines
 * whether the user sees an IVF, IUI, natural, or neutral journey. When
 * modality is unknown (not yet selected), the user stays on an exploratory
 * journey until they make an explicit choice.
 */

import type { HomepageGender, HomepageGoal } from "../../homepage.types";
import type {
  JourneyDefinition,
  JourneyKey,
  Pathway,
  TreatmentModality,
  JourneyViewerRole,
} from "../plan.types";
import {
  EGG_FREEZING_GUIDE_ARTICLE_ID,
  EGG_FREEZING_SOLO,
} from "./egg-freezing-solo";
import { EMBRYO_FREEZING_PARTNER } from "./embryo-freezing-partner";
import { IVF_PARTNER } from "./ivf-partner";
import { IUI_PARTNER } from "./iui-partner";
import { SPERM_FREEZING_SOLO } from "./sperm-freezing-solo";
import { IVF_SOLO } from "./ivf-solo";
import { IVF_DONOR } from "./ivf-donor";
import { IVF_SURROGATE } from "./ivf-surrogate";
import { IVF_PARTNER_MALE } from "./ivf-partner-male";
import { IVF_SOLO_MALE } from "./ivf-solo-male";
import { IVF_DONOR_MALE } from "./ivf-donor-male";
import { IVF_SURROGATE_MALE } from "./ivf-surrogate-male";
import { IUI_PARTNER_MALE } from "./iui-partner-male";
import { IUI_DONOR, IUI_DONOR_MALE, IUI_SOLO } from "./iui-participant";
import { EGG_DONATION, EGG_DONATION_GUIDE_ARTICLE_ID } from "./egg-donation";
import { SPERM_DONATION } from "./sperm-donation";
import { TRYING_NATURALLY } from "./trying-naturally";
import { PREGNANCY_UNDECIDED } from "./pregnancy-undecided";
import { PREGNANCY_EXPLORING } from "./pregnancy-exploring";

export const JOURNEY_REGISTRY: Record<JourneyKey, JourneyDefinition> = {
  "trying-naturally": TRYING_NATURALLY,
  "pregnancy-undecided": PREGNANCY_UNDECIDED,
  "pregnancy-exploring": PREGNANCY_EXPLORING,
  "egg-freezing-solo": EGG_FREEZING_SOLO,
  "embryo-freezing-with-partner": EMBRYO_FREEZING_PARTNER,
  "embryo-freezing-with-donor": EMBRYO_FREEZING_PARTNER, // Base, donor variant added later
  "ivf-solo": IVF_SOLO,
  "ivf-with-partner": IVF_PARTNER,
  "ivf-with-donor": IVF_DONOR,
  "ivf-with-surrogate": IVF_SURROGATE,
  "ivf-solo-male": IVF_SOLO_MALE,
  "ivf-with-partner-male": IVF_PARTNER_MALE,
  "ivf-with-donor-male": IVF_DONOR_MALE,
  "ivf-with-surrogate-male": IVF_SURROGATE_MALE,
  "iui-solo": IUI_SOLO,
  "iui-with-partner": IUI_PARTNER,
  "iui-with-donor": IUI_DONOR,
  "iui-with-partner-male": IUI_PARTNER_MALE,
  "iui-with-donor-male": IUI_DONOR_MALE,
  "sperm-freezing-solo": SPERM_FREEZING_SOLO,
  "egg-donation": EGG_DONATION,
  "sperm-donation": SPERM_DONATION,
};

/**
 * Resolve a journey key from the user's state.
 *
 * For pregnancy, `modality` determines IVF vs IUI vs others.
 * When modality is not specified, keep the plan exploratory. An unanswered
 * treatment choice must never produce an IVF journey.
 */
export function resolveJourneyKey(
  gender: HomepageGender | null,
  goal: HomepageGoal | null,
  pathway: Pathway,
  modality?: TreatmentModality | null,
  viewerRole?: JourneyViewerRole | null,
): JourneyKey {
  // Incomplete profiles are rendered by the setup/explorer Home. Keep the
  // legacy plan hook harmless while that route is being resolved instead of
  // manufacturing an egg- or sperm-freezing journey.
  if (!gender || !goal) return "pregnancy-undecided";

  // Female preserve → egg freezing (partner is a settings-level addition, not a separate journey)
  if (gender === "female" && goal === "preserve") {
    return "egg-freezing-solo";
  }

  // Male preserve → sperm freezing
  if (gender === "male" && goal === "preserve") {
    return "sperm-freezing-solo";
  }

  // Embryo preserve
  if (goal === "embryo-preserve") {
    return pathway === "with-donor"
      ? "embryo-freezing-with-donor"
      : "embryo-freezing-with-partner";
  }

  // Pregnancy — modality determines the treatment journey, gender determines perspective
  if (goal === "pregnancy") {
    if (modality === "natural") return "trying-naturally";
    if (modality === "iui") {
      // A donor participant is on a source workflow, not the recipient's
      // carrying workflow. The donor journey contains no partner identity or
      // shared-cycle data.
      if (viewerRole === "donor") {
        return gender === "male" ? "iui-with-donor-male" : "egg-donation";
      }
      // Partner projection is only meaningful once the API has identified
      // the viewer as a participant. An unresolved relationship must not turn
      // gender plus a shared cycle into partner/sample-provider tasks.
      if (viewerRole === "partner") {
        return gender === "male"
          ? "iui-with-partner-male"
          : "pregnancy-exploring";
      }
      if (viewerRole === "carrier" || viewerRole === "intended-parent") {
        return "pregnancy-exploring";
      }
      if (pathway === "with-donor") {
        return gender === "female" ? "iui-with-donor" : "pregnancy-exploring";
      }
      if (pathway === "solo") {
        return gender === "female" ? "iui-solo" : "pregnancy-exploring";
      }
      // The male partner journey is a specialized, non-carrying view. Keep
      // an unresolved male viewer out of it until participant authorization is
      // explicit; the exploration journey is honest about what we know.
      if (gender === "male") return "pregnancy-exploring";
      return "iui-with-partner";
    }
    if (modality !== "ivf") return "pregnancy-exploring";
    // Explicit IVF selection.
    if (pathway === "solo") {
      return gender === "male" ? "ivf-solo-male" : "ivf-solo";
    }
    if (pathway === "with-surrogate") {
      return gender === "male"
        ? "ivf-with-surrogate-male"
        : "ivf-with-surrogate";
    }
    if (pathway === "with-donor") {
      return gender === "male" ? "ivf-with-donor-male" : "ivf-with-donor";
    }
    return gender === "male" ? "ivf-with-partner-male" : "ivf-with-partner";
  }

  // Donations
  if (goal === "egg-donation") return "egg-donation";
  if (goal === "sperm-donation") return "sperm-donation";

  // Default fallback
  return gender === "male" ? "sperm-freezing-solo" : "egg-freezing-solo";
}

/**
 * Get journey definition from the user's state.
 */
export function getJourneyForState(
  gender: HomepageGender | null,
  goal: HomepageGoal | null,
  pathway: Pathway,
  modality?: TreatmentModality | null,
  viewerRole?: JourneyViewerRole | null,
): JourneyDefinition {
  const key = resolveJourneyKey(gender, goal, pathway, modality, viewerRole);
  return JOURNEY_REGISTRY[key];
}

export {
  TRYING_NATURALLY,
  PREGNANCY_UNDECIDED,
  PREGNANCY_EXPLORING,
  EGG_FREEZING_SOLO,
  EGG_FREEZING_GUIDE_ARTICLE_ID,
  EMBRYO_FREEZING_PARTNER,
  IVF_SOLO,
  IVF_PARTNER,
  IVF_DONOR,
  IVF_SURROGATE,
  IVF_SOLO_MALE,
  IVF_PARTNER_MALE,
  IVF_DONOR_MALE,
  IVF_SURROGATE_MALE,
  IUI_PARTNER,
  IUI_PARTNER_MALE,
  IUI_SOLO,
  IUI_DONOR,
  IUI_DONOR_MALE,
  SPERM_FREEZING_SOLO,
  EGG_DONATION,
  SPERM_DONATION,
  EGG_DONATION_GUIDE_ARTICLE_ID,
};
